// Preview swatches use fixed colors on purpose; PDF/DOCX outputs are theme-invariant.
import React, { useMemo } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import { Text } from 'react-native-paper';
import { MaterialCommunityIcons } from '@expo/vector-icons';
import { useTranslation } from 'react-i18next';

import { TemplateId } from '../../types';
import { Palette, radius, spacing, useThemePalette } from '../../theme';
import { templateIds } from '../../lib/resume';

interface Props {
  value: TemplateId;
  onChange: (next: TemplateId) => void;
}

const Preview: React.FC<{ id: TemplateId }> = ({ id }) => {
  if (id === 'modern') {
    return (
      <View style={previewStyles.page}>
        <View style={previewStyles.modernSidebar}>
          <View style={[previewStyles.modernBar, { width: '70%' }]} />
          <View style={[previewStyles.modernBar, { width: '50%' }]} />
          <View style={[previewStyles.modernBar, { width: '60%' }]} />
        </View>
        <View style={previewStyles.modernBody}>
          <View style={[previewStyles.line, { width: '60%', height: 8 }]} />
          <View style={[previewStyles.accent, { width: 40 }]} />
          <View style={[previewStyles.line, { width: '90%' }]} />
          <View style={[previewStyles.line, { width: '85%' }]} />
          <View style={[previewStyles.line, { width: '70%' }]} />
        </View>
      </View>
    );
  }
  if (id === 'classic') {
    return (
      <View style={[previewStyles.page, previewStyles.classicPage]}>
        <View style={previewStyles.classicHeader}>
          <View style={[previewStyles.line, { width: '50%', height: 8, alignSelf: 'center' }]} />
          <View style={[previewStyles.line, { width: '30%', alignSelf: 'center' }]} />
        </View>
        <View style={previewStyles.classicRule} />
        <View style={[previewStyles.line, { width: '40%', height: 6 }]} />
        <View style={[previewStyles.line, { width: '95%' }]} />
        <View style={[previewStyles.line, { width: '90%' }]} />
        <View style={[previewStyles.line, { width: '80%' }]} />
      </View>
    );
  }
  return (
    <View style={[previewStyles.page, previewStyles.minimalPage]}>
      <View style={[previewStyles.line, { width: '55%', height: 10 }]} />
      <View style={[previewStyles.line, { width: '35%' }]} />
      <View style={previewStyles.minimalSpacer} />
      <View style={[previewStyles.line, { width: '20%', height: 4 }]} />
      <View style={[previewStyles.minimalUnderline]} />
      <View style={[previewStyles.line, { width: '95%' }]} />
      <View style={[previewStyles.line, { width: '85%' }]} />
      <View style={[previewStyles.line, { width: '70%' }]} />
    </View>
  );
};

const TemplatePicker: React.FC<Props> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const palette = useThemePalette();
  const styles = useMemo(() => makeStyles(palette), [palette]);

  return (
    <View style={styles.container}>
      {templateIds.map((id) => {
        const selected = id === value;
        return (
          <TouchableOpacity
            key={id}
            onPress={() => onChange(id)}
            activeOpacity={0.9}
            style={[styles.card, selected && styles.cardSelected]}
          >
            <Preview id={id} />
            <View style={styles.meta}>
              <View style={styles.metaHeader}>
                <Text style={styles.name}>{t(`resume.templates.${id}`)}</Text>
                {selected && (
                  <MaterialCommunityIcons
                    name="check-circle"
                    size={20}
                    color={palette.appliedText}
                  />
                )}
              </View>
              <Text style={styles.description}>
                {t(`resume.templates.${id}Description`)}
              </Text>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );
};

const previewStyles = StyleSheet.create({
  page: {
    height: 140,
    borderRadius: 6,
    overflow: 'hidden',
    flexDirection: 'row',
    backgroundColor: '#FFFFFF',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  modernSidebar: {
    width: '38%',
    backgroundColor: '#1E2A38',
    padding: 8,
    gap: 6,
  },
  modernBar: { height: 4, borderRadius: 2, backgroundColor: '#FFFFFF' },
  modernBody: { flex: 1, padding: 10, gap: 6 },
  accent: { height: 3, borderRadius: 2, backgroundColor: '#2BA77F' },
  line: { height: 4, borderRadius: 2, backgroundColor: '#374151' },
  classicPage: { flexDirection: 'column', padding: 12, gap: 6 },
  classicHeader: { gap: 6 },
  classicRule: {
    height: 1,
    backgroundColor: '#000',
    marginVertical: 4,
  },
  minimalPage: { flexDirection: 'column', padding: 14, gap: 6 },
  minimalSpacer: { height: 6 },
  minimalUnderline: {
    height: 1,
    width: 24,
    backgroundColor: '#000',
    marginBottom: 4,
  },
});

const makeStyles = (p: Palette) =>
  StyleSheet.create({
    container: { gap: spacing.md },
    card: {
      borderRadius: radius.md,
      backgroundColor: p.surface,
      borderWidth: 2,
      borderColor: p.border,
      overflow: 'hidden',
    },
    cardSelected: { borderColor: p.primary },
    meta: { padding: spacing.md, gap: spacing.xs },
    metaHeader: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    name: { fontSize: 15, fontWeight: '700', color: p.textPrimary },
    description: { fontSize: 12, color: p.textSecondary },
  });

export default TemplatePicker;
